import PageTitle from '../../components/PageTitle'
import Context from '../../context'
import ProductCard from '../../components/ProductCard'
import React, { useState, useEffect, useContext, useRef } from 'react'
import { InferGetServerSidePropsType } from 'next'
import getConfig from 'next/config'
import useDidMountEffect from '../../utils/useDidMountEffect'
import Head from 'next/head'
import BackToTop from '../../components/BackToTop'
import BreadCrumb from '../../components/BreadCrumb'
import { useAuthAPI } from '../../hooks/api.hooks'
import { useIonModal } from '@ionic/react'
import Filters from '../../components/Filters'
import { enterAnimation, leaveAnimation } from '../../utils/modal-animations'
import SVG from '../../components/SVG'
import FilterCount from '../../components/FilterCount'

const { publicRuntimeConfig } = getConfig()

const Thematics = ({
  products,
  meta,
  slug,
  numberOfResults
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { state } = useContext(Context)
  const [productsArr, setProductsArr] = useState(products)
  const [offset, setOffset] = useState(0)
  const [filters, setFilters] = useState({})
  const [totalRows, setTotalRows] = useState(numberOfResults)
  const [selectedFilters, setSelectedFilters] = useState({
    facets: {},
    sort: {}
  })
  const [animated, setAnimated] = useState(false)
  const [showFilterLoader, setFilterLoader] = useState(false)
  const isFirstRun = useRef(true)
  const filtersSet = Object.keys(selectedFilters.facets).length > 0
  const authAPICall = useAuthAPI()

  const removeFilterKeys = (facets) => {
    delete facets.avg_rating
    delete facets.rating_count
    return facets
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.br_data = window.br_data || {}
      window.br_data.acct_id = publicRuntimeConfig.BR_ID
      window.br_data.test_data = publicRuntimeConfig.BR_TEST_DATA
      window.br_data.ptype = 'thematic'
      window.br_data.view_id = state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE
      window.br_data.user_id = state.user.UID || state.deviceId;
      (() => {
        const brtrk = document.createElement('script')
        brtrk.type = 'text/javascript'
        brtrk.async = true
        brtrk.src = `//cdn.brcdn.com/v1/br-trk-${publicRuntimeConfig.BR_ID}.js?v=${Date.now()}`
        const s = document.getElementsByTagName('script')[0]
        s.parentNode.insertBefore(brtrk, s)
      })()
    }
  }, [])

  useEffect(() => {
    setOffset(0)
    setFilters({})

    if (isFirstRun.current) {
      isFirstRun.current = false
    } else {
      setSelectedFilters({
        facets: {},
        sort: {}
      })
    }
    authAPICall(
      'productinfo',
      'thematic',
      'GET',
      {
        slug,
        popkey: publicRuntimeConfig.POP_KEY,
        store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
        offset: 0
      },
      {}
    ).then((data) => {
      setProductsArr([...data.response])
      const newFacets = removeFilterKeys({ ...data.facets })
      setFilters(newFacets)
      setTotalRows(data.numFound)
    })
  }, [state.cart.store?.storenumber])

  useEffect(() => {
    if (offset !== 0) {
      const filters = JSON.stringify(selectedFilters.facets)
      const sorting = JSON.stringify(selectedFilters.sort)

      authAPICall(
        'productinfo',
        'thematic',
        'GET',
        {
          slug,
          popkey: publicRuntimeConfig.POP_KEY,
          offset,
          store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
          filters,
          sort: sorting
        },
        {}
      ).then((data) => {
        setAnimated(false)
        setProductsArr([...productsArr, ...data.response])
        setTotalRows(data.numFound)
      })
    }
  }, [offset])

  useDidMountEffect(() => {
    setOffset(0)
    setFilterLoader(true)
    const filters = JSON.stringify(selectedFilters.facets)
    const sorting = JSON.stringify(selectedFilters.sort)

    authAPICall(
      'productinfo',
      'thematic',
      'GET',
      {
        slug,
        popkey: publicRuntimeConfig.POP_KEY,
        offset: 0,
        store: state.cart.store?.storenumber || publicRuntimeConfig.DEFAULT_STORE,
        filters,
        sort: sorting
      },
      {}
    ).then((data) => {
      setProductsArr([...data.response])
      setTotalRows(data.numFound)
      setFilterLoader(false)
    })
  }, [selectedFilters])

  const renderCards = () => {
    if (productsArr.length) {
      return productsArr.map(item => <ProductCard data={item} key={item.slug} vertical={!grid} />)
    }
  }

  const [grid, setOpen] = React.useState(false)

  const submitForm = (filters, sort) => {
    setSelectedFilters({
      facets: { ...filters },
      sort: { ...sort }
    })
  }

  const handleDismiss = () => dismiss()
  const [present, dismiss] = useIonModal(Filters, { handleDismiss, data: filters, incomingData: { ...selectedFilters.facets }, incomingSort: selectedFilters.sort, submitForm, amt: productsArr.length ? totalRows : 0, loading: showFilterLoader }
  )
  const toggleFilters = async () => {
    present({ cssClass: 'modal--filters', enterAnimation, leaveAnimation })
  }

  return (
    <div>
      <div className='thematic-page'>
        <Head>
          <title>{meta.title}</title>
          <meta
            name="description"
            content={meta.meta_description}
          />
        </Head>
        <div className=" thematic-page__content">
          <div className="thematic-page__content__title-spacing" >
            <nav aria-label="Breadcrumb">
              <BreadCrumb props={['/', { slug, name: meta.h1 }]} theme />
            </nav>
            <PageTitle title={meta.h1} />
          </div>

          <div className="category-page__selectors">
            <FilterCount results={totalRows} />
            <div className="category-page__selectors-container">
              <div className="category-page__selectors-icons">
                <button className={grid ? 'activate' : ''} onClick={() => setOpen(!grid)}>
                  <SVG name='plp-grid' label='grid view' size='md' />
                </button>
                <button className={!grid ? 'activate' : ''} onClick={() => setOpen(!grid)}>
                  <SVG name='plp-list' label='list view' size='md' />
                </button>
              </div>
              <button className={`filters__button ${filtersSet ? 'filters__button-bg' : ''}`} onClick={toggleFilters}>
                <p>{filtersSet ? 'Filtered' : 'Filters'}</p>
              </button>
            </div>
          </div>
          <div className={`product-list-page ${grid ? 'product-list-page__grid' : ''}`}>
            {renderCards()}
          </div>

          <div className="thematic-page__content__view-more-container">
            <div className="thematic-page__content__view-more-container__view-more-btn">
              {!!productsArr.length && (
                <div className="thematic-page__content__view-more-container__view-more-btn__width">
                  {(((totalRows > (offset + 24))) || animated) && (
                    <button
                      onClick={(e) => {
                        if (!animated) {
                          setAnimated(true)
                          setOffset(offset + 24)
                        }
                      }}
                      className={`btn__secondary btn__secondary--full${animated ? ' btn__animated' : ''}`}
                    >
                      VIEW MORE
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
      <BackToTop />

    </div>
  )
}

export default Thematics

export const getServerSideProps = async ({ req, query }) => {
  const { publicRuntimeConfig } = getConfig()
  const res = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/productinfo/${publicRuntimeConfig.ENV}/thematic?slug=${query.slug}&popkey=${publicRuntimeConfig.POP_KEY}&store=${publicRuntimeConfig.DEFAULT_STORE}&offset=0`,
    {
      headers: {
        deviceid: ''
      }
    }
  )
  const items = await res.json()

  return {
    props: {
      products: items.response || [],
      meta: items.meta || {},
      slug: query.slug,
      numberOfResults: items.numFound || 0
    }
  }
}
